import logger from '@fhs-legacy/frontend/src/utils/logger';

import type { GooglePayContext, PipelineStepContext, PipelineStepResult } from '..';
import { PaymentProcessor, PipelineBaseStep } from '..';

/**
 * Converts Google Pay token for Worldpay
 *
 * For Worldpay, Google Pay returns a low value token in the paymentData field.
 * This step extracts that token and prepares it for use in the commit order payload.
 *
 * Worldpay expects:
 * - googlePayDetails.paymentData = Worldpay low value token
 * - googlePayDetails.signature = empty
 * - googlePayDetails.data = empty
 * - googlePayDetails.version = empty
 */
export class WorldpayGooglePayConversionStep extends PipelineBaseStep {
  readonly name = 'WorldpayGooglePayConversionStep';

  async execute(context: PipelineStepContext): Promise<PipelineStepResult> {
    // Only run for Google Pay with Worldpay
    if (context.paymentMethod !== 'googlePay' || context.processor !== PaymentProcessor.WORLDPAY) {
      return this.success();
    }

    const googlePayContext = context as GooglePayContext;
    const { googlePayDetails } = googlePayContext;

    logger.info({
      message: '[GooglePay] Converting token for Worldpay',
      processor: context.processor,
      hasPaymentData: !!googlePayDetails.paymentData,
    });

    // For Worldpay, the paymentData contains the low value token
    // We need to ensure signature, data, and version are empty
    const convertedDetails = {
      ...googlePayDetails,
      signature: '',
      data: '',
      version: '',
      // paymentData already contains the Worldpay low value token
    };

    logger.info({
      message: '[GooglePay] Worldpay token conversion complete',
      tokenLength: convertedDetails.paymentData?.length || 0,
      paymentType: convertedDetails.PaymentMethodData.paymentType,
    });

    // Return updated context with converted details
    return this.success({
      googlePayDetails: convertedDetails,
    } as Partial<GooglePayContext>);
  }
}
